import { Injectable, OnDestroy } from '@angular/core';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { TranslateService } from '@ngx-translate/core';
import { ITableData } from '../../../models/reports/ITableData';
import { IndexDBService } from '../../common/indexDB.service';
import { EstimateMetaData, GetDevicesCpCostRequest, GetDevicesCpCostResponse, GetPartsCpCostRequest, GetPartsCpCostResponse, Material, MaterialData, OverTimePWRAdjustment, ProductPromotionModel, SQWebArea, SQWebSystem, SubSystem, SystemChildren, TypicalOfSystemHelper } from '../../../models/dtos';
import { IDataItem } from '../../../models/reports/IDataItem';
import { CostOverrideData } from '../../../models/common/SpecialPricingTableData';
import { IData } from '../../../models/reports/IData';
import { IFooter } from '../../../models/reports/IFooter';
import { ReportCheckboxes } from '../../../models/reports/ReportCheckboxes';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { CommonDataService } from '../../common-data.service';
import { ReportsAPIService } from '../../api/reports-api.service';
import { firstValueFrom } from 'rxjs';
import { IDevicesAndPartsCpCost } from '../../../models/reports/IDevicesAndPartsCpCost';
import { roundToPrecision } from '../../../../types/number-extensions';

@Injectable()
export class SpecialPricingReportService implements OnDestroy {

  costDiscountData: CostOverrideData[] = [];
  costForOverrideData: CostOverrideData[] = [];
  labourOverrideReferenceNumbers: Set<string> = new Set();
  selectedReportCheckboxes: ReportCheckboxes;
  private typicalOfHelper: Record<string, TypicalOfSystemHelper> = {};
  private processedTypicalIds: Set<string> = new Set();
  private projectAreas: SQWebArea[] = [];
  private projectMaterialData: Record<string, MaterialData>;
  overrideKeys = new Set<string>(); // for overrides table
  discountKeys = new Set<string>(); // for discount table
  APNSSNList: string[] = [];
  RPNSSNList: string[] = [];

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true): Promise<void> {
    const reportName = this.reportsCommonService.getSelectedReportName();
    const translatedReportName = reportName === undefined ? this.translate.instant('SPECIAL_PRICING_REPORT'):  this.translate.instant(reportName);
    this.selectedReportCheckboxes = this.reportsCommonService.getReportsCheckboxes();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    this.typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
    const reportData: IReportData = {
      selectionName: translatedReportName,
      reportTitle: translatedReportName,
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: await this.generateTableData(),
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }

  constructor(private readonly translate: TranslateService,
    private readonly commonDataService: CommonDataService,
    private readonly indexdb: IndexDBService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly reportApiService: ReportsAPIService
  ) { }

  async generateTableData(): Promise<ITableData[]> {
    this.costDiscountData = [];
    this.costForOverrideData = [];
    this.labourOverrideReferenceNumbers.clear();
    this.discountKeys.clear();
    this.overrideKeys.clear();
    await this.generateSequencedMaterialData();
    const discountTableData = await this.getDiscountMaterial();
    this.getProjectLevelHours();

    const tableData: ITableData[] = [];

    // Add subsidy table first if available
    if (discountTableData) {
      tableData.push(discountTableData);
    }

    // Choose columns depending on isExpanded
    const isExpanded = this.selectedReportCheckboxes.expanded.checked;
    const columns = isExpanded
      ? [this.translate.instant('MATERIAL_COST_OVERRIDES'),
      this.translate.instant('MODEL'),
      this.translate.instant('MANUFACTURER'),
      this.translate.instant('VENDOR'),
      this.translate.instant('OVERRIDE_NOTES')]
      : [this.translate.instant('MATERIAL_COST_OVERRIDES'),
      this.translate.instant('VENDOR'),
      this.translate.instant('OVERRIDE_NOTES')];

    // Title row (column headers)
    const titleRow: IDataItem[] = columns.map((col, index) => {
      const isExpandedCol = isExpanded && (index === 1 || index === 2); // MODEL, MANUFACTURER
      return {
        data: col,
        columnspan: CONSTANTS.ONE,
        hierarchyLevel: CONSTANTS.ONE,
        DataCellColor: index === CONSTANTS.ZERO
          ? CONSTANTS.REPORT_ORANGE_COLOR
          : CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
        alignment: CONSTANTS.CENTER_ALIGNMENT,
        isExpanded: isExpandedCol
      };
    });

    // Data rows
    const dataRows = this.costForOverrideData.map(item => {
      const rowItems: IDataItem[] = [];
      // Reference
      rowItems.push({ data: item.referenceNumber, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT });

      if (isExpanded) {    
        rowItems.push(
          { // Model (expanded)
            data: item.model = item.model ?? CONSTANTS.EMPTY_STRING,
            columnspan: CONSTANTS.ONE,
            hierarchyLevel: 0,
            alignment: CONSTANTS.LEFT_ALIGNMENT,
            isExpanded: true
          },
          { // Manufacturer (expanded)
            data: item.manufacturer = item.manufacturer ?? CONSTANTS.EMPTY_STRING,
            columnspan: CONSTANTS.ONE,
            hierarchyLevel: 0,
            alignment: CONSTANTS.LEFT_ALIGNMENT,
            isExpanded: true
          }
        );
      }

      rowItems.push(
        { // Vendor
          data: item.vendor, 
          columnspan: CONSTANTS.ONE, 
          hierarchyLevel: 0, 
          alignment: CONSTANTS.LEFT_ALIGNMENT 
        },
        { // Notes
          data: item.notes, 
          columnspan: CONSTANTS.ONE, 
          hierarchyLevel: 0, 
          alignment: CONSTANTS.LEFT_ALIGNMENT 
        }
      );
      return { data: rowItems };
    });

    // Compose the table  
    if (!dataRows.length) {
      return [];
    }
    
    tableData.push({
      tableHeader: [],
      title: titleRow,
      data: dataRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    });

    // Labour override table (two columns: NonStraight Time Labor Rate | Used)
    const labourTitleRow: IDataItem[] = [
      {
        data: this.translate.instant('NONSTRAIGHT_TIME_LABOR_RATE'),
        columnspan: CONSTANTS.ONE,
        hierarchyLevel: CONSTANTS.ONE,
        DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
        alignment: CONSTANTS.LEFT_ALIGNMENT
      },
      {
        data: this.translate.instant('USED'),
        columnspan: CONSTANTS.ONE,
        hierarchyLevel: CONSTANTS.ONE,
        DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
        alignment: CONSTANTS.CENTER_ALIGNMENT
      }
    ];

    // Pre-process all references to lowercase once for O(n) complexity instead of O(n×m)
    const lowerCaseReferences = Array.from(this.labourOverrideReferenceNumbers)
      .filter(Boolean)
      .map((ref: string) => ref.toLowerCase());

    if (lowerCaseReferences.length > CONSTANTS.ZERO) {

      // Labour rows mapping 
      const labourRowsMap: { label: string; match: string }[] = [
        { label: this.translate.instant('SHIFT_DIFFERENTIAL_RATE'), match: this.translate.instant('1.1X_AREA_LEVEL_ADJUSTED_HOURS') },
        { label: this.translate.instant('OVERTIME_TIME_AND_HALF'), match: this.translate.instant('1.5X_AREA_LEVEL_ADJUSTED_HOURS') },
        { label: this.translate.instant('OVERTIME_DOUBLE_TIME'), match: this.translate.instant('2X_AREA_LEVEL_ADJUSTED_HOURS') },
        { label: this.translate.instant('PREVAILING_WAGE_RATE'), match: this.translate.instant('PREVAILING_WAGE_RATE_PWR') },
        { label: this.translate.instant('PWR_SHIFT_DIFFERENTIAL_RATE'),
          match: this.translate.instant('PREVAILING_WAGE_RATE_PWR') + ' ' + this.translate.instant('1.1X_AREA_LEVEL_ADJUSTED_HOURS') },
        { label: this.translate.instant('PREVAILING_WAGE_RATE_TIME_AND_HALF'), 
          match: this.translate.instant('PREVAILING_WAGE_RATE_PWR') + ' ' + this.translate.instant('1.5X_AREA_LEVEL_ADJUSTED_HOURS') },
        { label: this.translate.instant('PREVAILING_WAGE_RATE_DOUBLE_TIME'), 
          match: this.translate.instant('PREVAILING_WAGE_RATE_PWR') + ' ' + this.translate.instant('2X_AREA_LEVEL_ADJUSTED_HOURS') },
      ];

      // Build labour data rows by checking if any stored reference matches
      const labourDataRows: IData[] = labourRowsMap.map(row => {
        const used = lowerCaseReferences.some(ref => ref.includes(row.match.toLowerCase()));

        return {
          data: [
            { data: row.label, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: used ? this.translate.instant('YES') : CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT }
          ]
        };
      });

      tableData.push({
        tableHeader: [],
        title: labourTitleRow,
        data: labourDataRows,
        tableFooter: [],
        isPageBreakAfter: false,
        isTableBordered: true
      });
    }
    return tableData;
  }

  private getProjectLevelHours(): void {
    this.indexdb.getAllLaborData().then(laborData => {
      if (laborData?.laborsCollection) {
        const laborsCollection = laborData.laborsCollection;
        for (const laborEntry of Object.values(laborsCollection)) {
          let otCollection = this.commonDataService.globalPPMmode ? laborEntry.ppmSalesOfficesOtPwrAdjustmentsCollection : laborEntry.stdSalesOfficesOtPwrAdjustmentsCollection;
          if (otCollection) {
            for (const otPwrSalesOffices of otCollection) {
              const adjustments = otPwrSalesOffices.overTimePWRAdjustments;
              for (const otPwrRow of adjustments) {
                if (otPwrRow.referenceNumber !== this.translate.instant('STRAIGHT')) {
                  this.checkNonZeroHours(otPwrRow, otPwrRow.referenceNumber);
                }
              }
            }
          }
        }
      }
    });
  }
  private checkNonZeroHours(otPwrRow: OverTimePWRAdjustment, reference: string): void {
    const laborFields = [
      otPwrRow.projectManagementLabor,
      otPwrRow.engineerLabor,
      otPwrRow.specialistLabor,
      otPwrRow.mechanicalEngineerLabor,
      otPwrRow.electricalEngineerLabor,
      otPwrRow.digitalDeploymentLabor,
      otPwrRow.graphicsLabor,
      otPwrRow.programmingLabor
    ];

    if (laborFields.some(labor => labor > 0)) {
      this.labourOverrideReferenceNumbers.add(reference);
    }
  }

  private async getOriginalCost(): Promise<IDevicesAndPartsCpCost> {
    let devicesCpCost: GetDevicesCpCostResponse[] = [];
    let partsCpCost: GetPartsCpCostResponse[] = [];
    const business = this.commonDataService.getBusiness() || '';
    const cultureId = this.commonDataService.getEstimateCultureId() || 1;
    if (this.RPNSSNList.length) {
      const deviceRequest: GetDevicesCpCostRequest = {
        ssnList: this.RPNSSNList.join(','),
        business: business,
        shouldSkipBusiness: true, // for special pricing , this field always will be true
        cultureId: cultureId
      };

      devicesCpCost = await firstValueFrom(this.reportApiService.getDevicesCpCost(deviceRequest));
    }

    if (this.APNSSNList.length) {
      const partRequest: GetPartsCpCostRequest = {
        ssnList: this.APNSSNList.join(','),
        business: business,
        cultureId: cultureId
      };

      partsCpCost = await firstValueFrom(this.reportApiService.getPartsCpCost(partRequest));
    }

    return { devicesCpCost, partsCpCost };
  }

  private getOriginalCostBySSN(ssn: string, cpCostData: GetDevicesCpCostResponse[] | GetPartsCpCostResponse[]): number | undefined {
    const item = cpCostData.find(data => data.ssn === ssn);
    return item ? item.cost : undefined;
  }

  private async getDiscountMaterial(): Promise<ITableData | null> {
    const estimateMetaData: EstimateMetaData = this.commonDataService.getEstimateMetaData();
    const subsidy = estimateMetaData.estimateTab.subsidy ?? 0;
    const productpromotion: ProductPromotionModel[] = estimateMetaData.estimateSettings.productPromotionList;
    let totalDiscount = CONSTANTS.ZERO;
    let affectedMaterialText = CONSTANTS.EMPTY_STRING;
    let authorizationCode = CONSTANTS.EMPTY_STRING;
    let discount = CONSTANTS.ZERO;
    const sPromotion = productpromotion.find(pp => pp.affectedMaterial === 'S');
    if (sPromotion) {
      const {devicesCpCost, partsCpCost} = await this.getOriginalCost();
      affectedMaterialText = sPromotion.affectedMaterialText;
      authorizationCode = sPromotion.authorizationCode;
      discount = sPromotion.discount;
      for (const item of this.costDiscountData) {
        if (!item.isPPA || item.ppType !== 'S') { continue; }
        const actualCost = item.isRpn ? this.getOriginalCostBySSN(item.ssn, devicesCpCost) : this.getOriginalCostBySSN(item.ssn, partsCpCost);
        const originalCost = actualCost ? actualCost * item.quantity : item.actualCost * item.quantity;
        const discountFinding = (originalCost / 100) * sPromotion.discount;
        totalDiscount += discountFinding;
      }
    }

    if (subsidy || sPromotion) {
      // Title row (column headers)
      const titleRow: IDataItem[] = [
        {
          data: this.translate.instant('MATERIAL_DISCOUNT_PROGRAMS'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ONE,
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
          alignment: CONSTANTS.CENTER_ALIGNMENT
        },
        {
          data: this.translate.instant('AUTHORIZATION'),
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ONE,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.CENTER_ALIGNMENT
        },
        {
          data: '%',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ONE,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.CENTER_ALIGNMENT
        },
        {
          data: '$',
          columnspan: CONSTANTS.ONE,
          hierarchyLevel: CONSTANTS.ONE,
          DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
          alignment: CONSTANTS.CENTER_ALIGNMENT
        }
      ];


      const dataRows: IData[] = [];
      // Product promotion row (if any promotions)
      if (sPromotion) {
        dataRows.push({
          data: [
            { data: this.translate.instant('PRODUCT_PROMOTION'), columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT },
            { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT }
          ]
        }); // empty row for spacing

        dataRows.push({
          data: [
            { data: affectedMaterialText, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: authorizationCode, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: roundToPrecision(discount,  this.commonDataService.getDecimalPrecision()).toString(), columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT },
            { data: roundToPrecision(totalDiscount,  this.commonDataService.getDecimalPrecision()).toString(), columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT }
          ]
        });
      }
      // Subsidy row (if any)
      if (subsidy) {
        dataRows.push({
          data: [
            { data: this.translate.instant('SUBSIDY_PROGRAM'), columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
            { data: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT },
            { data: roundToPrecision(subsidy,  this.commonDataService.getDecimalPrecision()).toString(), columnspan: CONSTANTS.ONE, hierarchyLevel: 0, alignment: CONSTANTS.RIGHT_ALIGNMENT }
          ]
        });
      }

      // Footer row with total will come in action when we added product promotion list
      const footerRow: IFooter[] = [{
        headers: [
          { headerText: this.translate.instant('TOTAL_DISCOUNT_DOLLARS'), columnspan: 1, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { headerText: CONSTANTS.EMPTY_STRING, columnspan: 1, alignment: CONSTANTS.LEFT_ALIGNMENT },
          { headerText: CONSTANTS.EMPTY_STRING, columnspan: 1, alignment: CONSTANTS.RIGHT_ALIGNMENT },
          { headerText: roundToPrecision(subsidy + totalDiscount,  this.commonDataService.getDecimalPrecision()).toString(), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.RIGHT_ALIGNMENT }
        ]
      }];

      return {
        tableHeader: [],
        title: titleRow,
        data: dataRows,
        tableFooter: footerRow,
        isPageBreakAfter: false,
        isTableBordered: true
      };
    }

    return null;
  }

  async generateSequencedMaterialData() {
    this.projectAreas = this.commonDataService.getSQWebProject()?.areas || [];
    this.projectMaterialData = await this.indexdb.getAllMaterialData().then(data => data?.materialsCollection || {});
    this.projectAreas.forEach((area) => {
      this.iterateAreas(area);
    });
  }

  iterateAreas(area: SQWebArea) {
    if (area?.isItemVisible && area?.children) {
      for (let areaChild of area.children as SQWebAreaChildren[]) {
        if (areaChild?.nodeType && areaChild?.itemUniqueId) {
          switch (areaChild.nodeType) {
            case CONSTANTS.NODETYPE_AREA:
              this.iterateAreas(areaChild as SQWebArea);
              break;
            case CONSTANTS.NODETYPE_BSETSYSTEM:
              this.handleTypicalOf(areaChild as SQWebSystem);
              break;
            default:
              this.formatReportData(areaChild.itemUniqueId);
              break;
          }
        }
      }
    }
  }

  formatReportData(materialUniqueId: string, typicalOfCount: number = 1): void {
    if (!materialUniqueId || !this.projectMaterialData || !this.projectMaterialData[materialUniqueId]) {
      return;
    }
    let materialItemsCollection = this.projectMaterialData[materialUniqueId].systemChildrenCollection;
    this.formatMaterialData(materialItemsCollection, materialUniqueId, typicalOfCount);
  }

  formatMaterialData(materialItemsCollection: SystemChildren, materialUniqueId: string, typicalOfCount: number): void {
    materialItemsCollection?.forEach((materialItem) => {
      if (materialItem.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        (materialItem as SubSystem).children.forEach((childMaterialItem) => {
          this.generateDiscountDataAndOverrideData(childMaterialItem as Material, materialUniqueId, typicalOfCount);
        });
      } else {
        this.generateDiscountDataAndOverrideData(materialItem as Material, materialUniqueId, typicalOfCount);
      }
    });
  }

  private generateDiscountDataAndOverrideData(materialItem: Material, materialUniqueId: string, typicalOfCount: number): void {
    if (materialItem.quantity) {
      this.addDiscount(materialItem, typicalOfCount);
      if (materialItem.costOverride) {
        this.addOverrides(materialItem, materialUniqueId);
      }
    }
  }

  private addDiscount(material: Material, typicalOfCount: number): void {
    const key = `${material.referenceNumber}|${material.priceNote}`;
    if (this.discountKeys.has(key)) { 
      this.costDiscountData.forEach(item => {
        if (item.referenceNumber === material.referenceNumber && item.notes === material.priceNote) {
          item.actualCost += material.cost;
          item.cpCost += material.cpCost;
          item.PPMCost += material.ppmCost;
          item.quantity += material.quantity;
        }
      });
      return;
    }
    this.discountKeys.add(key);
    if(material.isPPA && material.ppType === 'S') {
      if(material.isRPN) {
        this.RPNSSNList.push(material.ssn);
       } else {
         this.APNSSNList.push(material.ssn);
       }
    }

    this.costDiscountData.push({
      isRpn: material.isRPN,
      ssn: material.ssn,
      referenceNumber: material.referenceNumber,
      model: material.modelNumber,
      manufacturer: material.mpn,
      vendor: material.vendorName,
      notes: material.priceNote,
      actualCost: material.cost,
      isPPA: material.isPPA,
      ppType: material.ppType,
      cpCost: material.cpCost,
      PPMCost: material.ppmCost,
      origin: material.orig,
      quantity: material.quantity * typicalOfCount
    });
  }

  private addOverrides(material: Material, itemUniqueId: string): void {
    const key = `${material.referenceNumber}|${material.priceNote}|${itemUniqueId}`;
    
    if (this.overrideKeys.has(key)) { return; }
    this.overrideKeys.add(key);

    const baseOverride: CostOverrideData = {
      isRpn: material.isRPN,
      ssn: material.ssn,
      referenceNumber: material.referenceNumber,
      vendor: material.vendorName,
      notes: material.priceNote,
      actualCost: material.cost,
      isPPA: material.isPPA,
      ppType: material.ppType,
      cpCost: material.cpCost,
      PPMCost: material.ppmCost,
      origin: material.orig,
      quantity: material.quantity
    };

    if (this.selectedReportCheckboxes.expanded.checked) {
      this.costForOverrideData.push({
        ...baseOverride,
        model: material.modelNumber,
        manufacturer: material.mpn
      });
    } else {
      this.costForOverrideData.push(baseOverride);
    }
  }

   handleTypicalOf(system: SQWebSystem){
    if(system.hasTypicalOf) {
      const typicalId = system.systemTypicalOfHelperUniqueId;
      const typicalOf = this.typicalOfHelper[typicalId];
      const typicalOfCount = typicalOf?.typicalOfSystems?.length ?? 1;
      const typicalOffUniqueId = typicalOf.typicalOfCollectionHolderId;
      if(!this.processedTypicalIds.has(typicalId)) {
        this.processedTypicalIds.add(typicalId);
        this.formatReportData(typicalOffUniqueId, typicalOfCount);
      }
    } else {
      this.formatReportData(system.itemUniqueId);
    }
  }



  ngOnDestroy(): void {
    this.costDiscountData = [];
    this.costForOverrideData = [];
    this.labourOverrideReferenceNumbers.clear();
    this.selectedReportCheckboxes = {} as ReportCheckboxes;
    this.processedTypicalIds.clear();
    this.projectAreas = [];
    this.projectMaterialData = {};
    this.overrideKeys.clear();
    this.discountKeys.clear();
    this.APNSSNList = [];
    this.RPNSSNList = [];
  }
}
